'use client'

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState
} from 'react'

import { validateAndCheckPostalCode } from '../queries/useQueryDeliverySupport'
import { DeliverySupportCookies } from '../storage/deliverySupport'
import { DeliveryArea, isSameDeliveryArea } from '../submodules/area'
import { PostalDeliveryState } from '../types'

export interface DeliverySupportContextType {
  postalCodeAndCityContext: PostalDeliveryState
  isPostalCodeAndCitySupported: boolean
  clearPostalCode: () => void
  validateAndUpdateAddress: (
    payload: DeliveryArea,
    savePostalCode?: boolean
  ) => Promise<PostalDeliveryState>
}

export const DeliverySupportContext =
  createContext<DeliverySupportContextType | null>(null)

const emptyPostalCodeAndCityContext = {
  postalCode: '',
  city: '',
  isSupported: false,
  deliveryMethods: []
}

export const DeliverySupportContextProvider = ({
  children
}: {
  children: ReactNode
}) => {
  const [postalCodeAndCityContext, setPostalCodeAndCityContext] =
    useState<PostalDeliveryState>(emptyPostalCodeAndCityContext)

  const postalCodeAndCityContextRef = useRef(postalCodeAndCityContext)

  // Layout effect: the ref has to be current before the passive effects of
  // the providers below (cart delivery sync) call `validateAndUpdateAddress`.
  useLayoutEffect(() => {
    postalCodeAndCityContextRef.current = postalCodeAndCityContext
  }, [postalCodeAndCityContext])

  const validateAndUpdateAddress = useCallback(
    async (
      { postalCode, city }: DeliveryArea,
      savePostalCode = true
    ): Promise<PostalDeliveryState> => {
      if (postalCode === '') {
        throw new Error('Kod pocztowy jest wymagany')
      }

      const currentContext = postalCodeAndCityContextRef.current

      const isSameAddress = isSameDeliveryArea(
        { postalCode, city },
        currentContext
      )

      if (isSameAddress && currentContext.isSupported) {
        return currentContext
      }

      const validation = city
        ? await validateAndCheckPostalCode(postalCode, city)
        : { isSupported: false, data: null, deliveryMethodId: null }

      const payload = {
        postalCode,
        city,
        isSupported: validation.isSupported,
        deliveryMethods: validation.data || []
      }

      if (savePostalCode) {
        setPostalCodeAndCityContext(payload)
        DeliverySupportCookies.set(payload)
      }

      return payload
    },
    []
  )

  const clearPostalCode = useCallback(() => {
    setPostalCodeAndCityContext(emptyPostalCodeAndCityContext)
    DeliverySupportCookies.set(emptyPostalCodeAndCityContext)
  }, [])

  return (
    <DeliverySupportContext.Provider
      value={{
        postalCodeAndCityContext,
        isPostalCodeAndCitySupported: postalCodeAndCityContext.isSupported,
        clearPostalCode,
        validateAndUpdateAddress: validateAndUpdateAddress
      }}
    >
      {children}
    </DeliverySupportContext.Provider>
  )
}

export const useDeliverySupport = () =>
  useContext(DeliverySupportContext) as DeliverySupportContextType
